import { isSealedItem, markdownToPlainText, type CanvasItem, type ItemType, type Space } from "@bedrock/domain";

export interface SearchResult {
  entityType: "space" | "item";
  entityId: string;
  spaceId: string;
  title: string;
  excerpt: string;
  itemType?: ItemType;
  spaceTitle?: string;
}

interface SearchDocument {
  result: SearchResult;
  title: string;
  body: string;
  keywords: string;
}

interface RankedResult {
  result: SearchResult;
  score: number;
}

const kindKeywords: Record<ItemType, string> = {
  "sticky-note": "sticky note memo",
  document: "document note page",
  label: "label heading",
  image: "image photo picture",
  video: "video movie",
  pdf: "pdf document file portable document",
  spreadsheet: "spreadsheet workbook excel xls xlsx file",
  "web-clip": "web clip website link bookmark",
  "date-event": "event calendar date time appointment schedule",
  stack: "folder stack collection",
  arrow: "arrow connector",
  frame: "frame section group"
};

const segmenterPattern = /[\p{L}\p{N}]+/gu;

/** Produces the same locale-independent representation for queries and indexed text. */
export function normalizeSearchText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .match(segmenterPattern)
    ?.join(" ") ?? "";
}

function uniqueTerms(value: string): string[] {
  return [...new Set(value.split(" ").filter(Boolean))];
}

function editDistanceAtMostOne(left: string, right: string): boolean {
  if (Math.abs(left.length - right.length) > 1) return false;
  if (left === right) return true;
  if (left.length === right.length) {
    const differences: number[] = [];
    for (let index = 0; index < left.length; index += 1) {
      if (left[index] !== right[index]) differences.push(index);
      if (differences.length > 2) return false;
    }
    if (differences.length <= 1) return true;
    const [first, second] = differences;
    if (first === undefined || second === undefined) return false;
    return second === first + 1 && left[first] === right[second] && left[second] === right[first];
  }
  const [shorter, longer] = left.length < right.length ? [left, right] : [right, left];
  let shortIndex = 0;
  let longIndex = 0;
  let skipped = false;
  while (shortIndex < shorter.length && longIndex < longer.length) {
    if (shorter[shortIndex] === longer[longIndex]) {
      shortIndex += 1;
      longIndex += 1;
    } else {
      if (skipped) return false;
      skipped = true;
      longIndex += 1;
    }
  }
  return true;
}

function tokenMatchQuality(queryTerm: string, candidateTerm: string): number {
  if (queryTerm === candidateTerm) return 1;
  if (queryTerm.length >= 2 && candidateTerm.startsWith(queryTerm)) return .82;
  if (queryTerm.length >= 3 && candidateTerm.includes(queryTerm)) return .62;
  if (queryTerm.startsWith(candidateTerm)) return 0;
  if (queryTerm.length >= 4 && candidateTerm.length >= 4 && editDistanceAtMostOne(queryTerm, candidateTerm)) return .48;
  return 0;
}

function bestTermScore(queryTerm: string, terms: string[]): number {
  let best = 0;
  for (const candidate of terms) best = Math.max(best, tokenMatchQuality(queryTerm, candidate));
  return best;
}

function scoreDocument(document: SearchDocument, normalizedQuery: string, queryTerms: string[]): number | null {
  const normalizedTitle = normalizeSearchText(document.title);
  const normalizedBody = normalizeSearchText(document.body);
  const normalizedKeywords = normalizeSearchText(document.keywords);
  const titleTerms = uniqueTerms(normalizedTitle);
  const bodyTerms = uniqueTerms(normalizedBody);
  const keywordTerms = uniqueTerms(normalizedKeywords);
  let score = 0;

  for (const queryTerm of queryTerms) {
    const titleScore = bestTermScore(queryTerm, titleTerms);
    const bodyScore = bestTermScore(queryTerm, bodyTerms);
    const keywordScore = bestTermScore(queryTerm, keywordTerms);
    const best = Math.max(titleScore * 90, bodyScore * 38, keywordScore * 24);
    if (best === 0) return null;
    score += best;
  }

  if (normalizedTitle === normalizedQuery) score += 720;
  else if (normalizedTitle.startsWith(normalizedQuery)) score += 480;
  else if (normalizedTitle.includes(normalizedQuery)) score += 320;
  if (normalizedBody.includes(normalizedQuery)) score += 180;
  if (normalizedKeywords === normalizedQuery) score += 80;
  if (queryTerms.every((term) => bestTermScore(term, titleTerms) > 0)) score += 130;
  return score;
}

function firstUsefulLine(value: string, fallback: string): string {
  const line = value.replace(/\s+/g, " ").trim();
  if (!line) return fallback;
  return line.length > 72 ? `${line.slice(0, 69).trimEnd()}…` : line;
}

function stickyTitle(markdown: string, plainText: string): string {
  const firstMarkdownLine = markdown
    .split(/\r?\n/)
    .map((line) => markdownToPlainText(line).replace(/[*_`~]/g, "").trim())
    .find(Boolean);
  return firstUsefulLine(firstMarkdownLine || plainText, "Untitled note");
}

function itemDocument(item: CanvasItem, spaceTitle: string): SearchDocument {
  const props = item.props;
  let title: string;
  let body = "";
  let keywords = `${kindKeywords[item.type]} ${spaceTitle}`;

  switch (props.kind) {
    case "sticky-note":
      title = props.lock ? "Locked note" : stickyTitle(props.markdown, props.plainText);
      body = props.plainText;
      if (props.lock) keywords += " locked";
      break;
    case "document":
      title = props.title.trim() || "Untitled document";
      body = props.plainText;
      if (props.lock) keywords += " locked";
      break;
    case "label":
      title = props.text.trim() || "Untitled label";
      break;
    case "image":
      title = props.filename.trim() || "Untitled image";
      body = props.alt;
      break;
    case "video":
      title = props.filename.trim() || "Untitled video";
      break;
    case "pdf":
      title = props.filename.trim() || "Untitled PDF";
      break;
    case "spreadsheet":
      title = props.filename.trim() || "Untitled workbook";
      break;
    case "web-clip":
      title = props.title.trim() || new URL(props.url).hostname;
      body = `${props.description} ${props.siteName}`;
      keywords += ` ${props.url}`;
      break;
    case "date-event":
      title = props.title.trim() || "Untitled event";
      body = [props.date, props.time].filter(Boolean).join(" ");
      break;
    case "stack":
      title = props.title.trim() || "Untitled folder";
      break;
    case "arrow":
      title = "Arrow";
      keywords += ` ${props.path} ${props.stroke} ${props.color}`;
      break;
    case "frame":
      title = props.title.trim() || "Untitled frame";
      break;
  }

  return {
    result: {
      entityType: "item",
      entityId: item.id,
      spaceId: item.spaceId,
      itemType: item.type,
      spaceTitle,
      title,
      excerpt: ""
    },
    title,
    body,
    keywords
  };
}

function withoutRepeatedTitle(body: string, title: string): string {
  const trimmedBody = body.trim();
  if (!trimmedBody.toLowerCase().startsWith(title.trim().toLowerCase())) return trimmedBody;
  return trimmedBody.slice(title.trim().length).replace(/^[\s:;,.\-–—]+/, "").trim();
}

function excerptAroundMatch(value: string, queryTerms: string[], maximumLength = 150): string {
  const text = value.replace(/\s+/g, " ").trim();
  if (!text) return "";
  let matchIndex = -1;
  for (const match of text.matchAll(segmenterPattern)) {
    const word = normalizeSearchText(match[0]);
    if (queryTerms.some((term) => tokenMatchQuality(term, word) > 0)) {
      matchIndex = match.index;
      break;
    }
  }
  if (text.length <= maximumLength) return text;
  let start = Math.max(0, (matchIndex < 0 ? 0 : matchIndex) - 44);
  if (start > 0) {
    const nextSpace = text.indexOf(" ", start);
    start = nextSpace < 0 ? start : nextSpace + 1;
  }
  let end = Math.min(text.length, start + maximumLength);
  if (end < text.length) {
    const previousSpace = text.lastIndexOf(" ", end);
    if (previousSpace > start) end = previousSpace;
  }
  return `${start > 0 ? "…" : ""}${text.slice(start, end)}${end < text.length ? "…" : ""}`;
}

/** Searches local entities with consistent matching and ranking across storage adapters. */
export function searchLocalContent(spaces: Space[], items: CanvasItem[], query: string, limit = 80): SearchResult[] {
  const normalizedQuery = normalizeSearchText(query.trim());
  if (!normalizedQuery) return [];
  const queryTerms = uniqueTerms(normalizedQuery);
  const activeSpaces = spaces.filter((space) => space.deletedAt === null);
  const spaceTitles = new Map(activeSpaces.map((space) => [space.id, space.title]));
  const documents: SearchDocument[] = [
    ...activeSpaces.map((space): SearchDocument => ({
      result: { entityType: "space", entityId: space.id, spaceId: space.id, title: space.title, excerpt: "" },
      title: space.title,
      body: "",
      keywords: "space workspace"
    })),
    ...items
      // Folder-sealed items have nothing readable to match; their folder's title is searchable instead.
      .filter((item) => item.deletedAt === null && spaceTitles.has(item.spaceId) && !isSealedItem(item))
      .map((item) => itemDocument(item, spaceTitles.get(item.spaceId) ?? ""))
  ];

  const ranked: RankedResult[] = [];
  for (const document of documents) {
    const score = scoreDocument(document, normalizedQuery, queryTerms);
    if (score === null) continue;
    const excerptBody = withoutRepeatedTitle(document.body, document.title);
    const excerpt = excerptBody
      ? excerptAroundMatch(excerptBody, queryTerms)
      : "";
    ranked.push({ result: { ...document.result, excerpt }, score });
  }

  return ranked
    .sort((left, right) => right.score - left.score
      || left.result.title.localeCompare(right.result.title)
      || left.result.entityId.localeCompare(right.result.entityId))
    .slice(0, limit)
    .map(({ result }) => result);
}
